import { useAuth0 } from '@auth0/auth0-react';
import { Suspense, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';

import { SocketProvider } from 'app/socket';
import { Loader } from 'shared';
import Box from 'shared_DEPRECATED/components/Box';
import { getPathParams } from 'shared_DEPRECATED/utils';

import { UNAUTHORIZED_PATHS } from 'features/auth/config';
import { clearSubAccountFromLocalStorage } from 'features/subAccounts/utils';
import { useUserContext } from 'features/user/context';
import { useUserMode } from 'features/user/hooks';
import { useUserTypeDefaultNavigate } from 'features/user/hooks/useUserTypeDefaultNavigate';
import { NewVersionBanner } from 'features/version/components/NewVersionBanner';

import { ModalContainer } from 'shared/components/Modal';
import { SidebarContainer } from 'shared/components/Sidebar';
import { localStorage } from 'shared/services/localStorage';

import { AuthenticatedLayout } from './AuthenticatedLayout';
import { UnauthenticatedLayout } from './UnauthenticatedLayout';

const USER_MODE_STORAGE_KEY =
  'selected-user-mode-' + process.env.REACT_APP_FRONTEND_ENVIRONMENT;

const Layout = () => {
  const userTypeDefaultNavigate = useUserTypeDefaultNavigate();
  const { pathname, search } = useLocation();

  const { user, isLoading: isUserLoading } = useUserContext();

  const {
    isLoading: isAuthLoading,
    isAuthenticated,
    loginWithRedirect,
    logout,
  } = useAuth0();

  const [firstPathParam] = getPathParams(pathname);
  const isUnauthorizedPath =
    Object.values(UNAUTHORIZED_PATHS).includes(firstPathParam);

  useEffect(() => {
    // Check for Auth0 errors and logout if found
    const urlParams = new URLSearchParams(search);
    const authError = urlParams.get('error');

    // Handle common Auth0 errors that should trigger logout
    if (authError) {
      // Clear any cached auth state and sub-account data, then redirect to logout
      clearSubAccountFromLocalStorage();
      logout({ returnTo: window.location.origin });
      return;
    }

    // Don't trigger login redirect if we're already handling an Auth0 callback
    const isAuth0Callback = urlParams.has('code') && urlParams.has('state');

    if (
      !isAuthLoading &&
      !isAuthenticated &&
      !isAuth0Callback &&
      !isUnauthorizedPath
    ) {
      loginWithRedirect({
        appState: {
          returnTo: `${pathname}${search}`,
        },
      });
    }
  }, [
    loginWithRedirect,
    logout,
    isAuthLoading,
    isAuthenticated,
    pathname,
    search,
    isUnauthorizedPath,
  ]);

  const userMode = useUserMode();

  useEffect(() => {
    if (userMode && isAuthenticated && pathname !== '/') {
      localStorage.setItem(USER_MODE_STORAGE_KEY, userMode);
    }
  }, [userMode, isAuthenticated, pathname]);

  useEffect(() => {
    if (isAuthenticated && pathname === '/' && !isUserLoading) {
      const storedUserMode = localStorage.getItem(USER_MODE_STORAGE_KEY);
      const userMode = user.type || storedUserMode;
      if (userMode) {
        userTypeDefaultNavigate(userMode);
      }
    }
  }, [
    isAuthenticated,
    userTypeDefaultNavigate,
    pathname,
    user.type,
    isUserLoading,
  ]);

  // Skip loading state for unauthorized paths
  if (!isUnauthorizedPath && (isUserLoading || isAuthLoading)) {
    return (
      <Box
        style={{
          width: '100%',
          height: '100vh',
        }}
      >
        <Loader />
      </Box>
    );
  }

  const LayoutComponent =
    isAuthenticated && user.type ? AuthenticatedLayout : UnauthenticatedLayout;

  return (
    <LayoutComponent>
      <SocketProvider>
        <Outlet />
        <ToastContainer style={{ width: 'auto' }} />
        <NewVersionBanner />
        <Suspense fallback={null}>
          <ModalContainer />
        </Suspense>
        <Suspense fallback={null}>
          <SidebarContainer />
        </Suspense>
      </SocketProvider>
    </LayoutComponent>
  );
};

export default Layout;
